import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Timer, CalendarDays, Users } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const inputCls = "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";
const EMPTY = {
  name: "", distance_km: "", fee: "", event_date: "", reporting_time: "", venue: "", max_slots: 0,
  registration_open: "", registration_close: "", min_age: 0, max_age: 0, description: "", terms_and_conditions: "", is_active: true,
};
const NUMS = ["distance_km", "fee", "max_slots", "min_age", "max_age"];

function F({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {children}
    </label>
  );
}

const ageText = (r) => (r.min_age && r.max_age ? `${r.min_age}–${r.max_age} yrs` : r.min_age ? `${r.min_age}+ yrs` : r.max_age ? `≤ ${r.max_age} yrs` : "No limit");

function RunDialog({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    const body = { ...form };
    NUMS.forEach((k) => { body[k] = Number(body[k]) || 0; });
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/admin/marathon/runs/${initial.id}`, body);
      else await api.post("/admin/marathon/runs", body);
      toast.success("Run saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save run");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mr-run-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{initial?.id ? "Edit Run" : "Add Run"}</DialogTitle>
          <DialogDescription>Fee and age limits are enforced by the server at registration and payment.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <F label="Run name" full><input className={inputCls} value={form.name} onChange={set("name")} placeholder="e.g. 10 KM Timed Run" data-testid="mr-name" /></F>
          <F label="Distance (KM)"><input type="number" min="0" step="0.1" className={inputCls} value={form.distance_km} onChange={set("distance_km")} data-testid="mr-distance" /></F>
          <F label="Fee (₹, incl. taxes)"><input type="number" min="0" className={inputCls} value={form.fee} onChange={set("fee")} data-testid="mr-fee" /></F>
          <F label="Event date"><input type="date" className={inputCls} value={form.event_date} onChange={set("event_date")} data-testid="mr-event-date" /></F>
          <F label="Reporting time"><input className={inputCls} value={form.reporting_time} onChange={set("reporting_time")} placeholder="05:30 AM" data-testid="mr-reporting" /></F>
          <F label="Venue" full><input className={inputCls} value={form.venue} onChange={set("venue")} data-testid="mr-venue" /></F>
          <F label="Registration opens"><input type="date" className={inputCls} value={form.registration_open} onChange={set("registration_open")} data-testid="mr-open" /></F>
          <F label="Registration closes"><input type="date" className={inputCls} value={form.registration_close} onChange={set("registration_close")} data-testid="mr-close" /></F>
          <F label="Minimum age (0 = none)"><input type="number" min="0" className={inputCls} value={form.min_age} onChange={set("min_age")} data-testid="mr-min-age" /></F>
          <F label="Maximum age (0 = none)"><input type="number" min="0" className={inputCls} value={form.max_age} onChange={set("max_age")} data-testid="mr-max-age" /></F>
          <F label="Max slots (0 = unlimited)"><input type="number" min="0" className={inputCls} value={form.max_slots} onChange={set("max_slots")} data-testid="mr-slots" /></F>
          <label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" checked={form.is_active} onChange={set("is_active")} data-testid="mr-active" />Active (visible on registration page)</label>
          <F label="Description" full>
            <RichTextEditor key={`d-${initial?.id || "new"}-${open}`} value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} placeholder="Short description of this run (shown on the landing page)…" testid="mr-description" />
          </F>
          <F label="Terms and Conditions / waiver (blank = default text)" full>
            <RichTextEditor key={`t-${initial?.id || "new"}-${open}`} value={form.terms_and_conditions} onChange={(v) => setForm((f) => ({ ...f, terms_and_conditions: v }))} placeholder="Terms and conditions, waiver and declaration for this run…" testid="mr-terms" />
          </F>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm" data-testid="mr-cancel">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="bg-[#234333] hover:bg-[#1a3326] text-white px-5 py-2.5 text-sm font-semibold rounded-sm disabled:opacity-60" data-testid="mr-save">{saving ? "Saving…" : "Save Run"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MarathonRuns() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/marathon/runs").then(({ data }) => setRuns(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/admin/marathon/runs/${r.id}`); toast.success("Run deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not delete"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-marathon-runs">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Marathon</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Run Types & Fees</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">Public registration page: <a href="/run/register" target="_blank" rel="noreferrer" className="text-[#234333] underline" data-testid="mr-public-link">/run/register</a> (not linked from the home page)</div>
        </div>
        <button type="button" onClick={() => { setEdit(null); setOpen(true); }} className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm" data-testid="mr-add-btn"><Plus size={16} />Add Run</button>
      </div>
      {loading ? <div className="text-sm text-[#6b655b]">Loading…</div> : runs.length === 0 ? (
        <div className="bg-white border border-[#e5e0d8] rounded-sm p-10 text-center text-sm text-[#6b655b]"><Timer size={34} className="mx-auto text-[#bcb6aa] mb-2" />No runs yet. Add 5 KM / 10 KM runs to open registration.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {runs.map((r) => (
            <div key={r.id} className="bg-white border border-[#e5e0d8] rounded-sm p-4" data-testid={`mr-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="font-serif text-4xl text-[#234333] leading-none">{r.distance_km}<span className="text-base ml-1">KM</span></div>
                <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm ${r.is_active ? "bg-[#e6efe9] text-[#234333]" : "bg-red-100 text-red-700"}`}>{r.is_active ? "Active" : "Inactive"}</span>
              </div>
              <div className="font-semibold mt-2">{r.name}</div>
              <div className="text-xl font-semibold mt-1" data-testid={`mr-card-fee-${r.id}`}>₹{Number(r.fee).toLocaleString("en-IN")}</div>
              <div className="mt-2 text-xs text-[#6b655b] space-y-1">
                <div className="flex items-center gap-1.5"><CalendarDays size={12} />Event {r.event_date} · Reg {r.registration_open} → {r.registration_close}</div>
                <div className="flex items-center gap-1.5"><Users size={12} />Age {ageText(r)} · Slots {r.max_slots || "∞"} · Paid {r.paid_count} · Pending {r.pending_count}</div>
              </div>
              <div className="mt-3 flex gap-1.5">
                <button type="button" onClick={() => { setEdit(r); setOpen(true); }} className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm" data-testid={`mr-edit-${r.id}`}><Pencil size={11} />Edit</button>
                <button type="button" onClick={() => remove(r)} className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm" data-testid={`mr-delete-${r.id}`}><Trash2 size={11} />Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <RunDialog open={open} initial={edit} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}
